import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Col } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, FormProvider } from "react-hook-form";

import "./manageAccessDialog.scss";
import testIds from "constants/testIds";
import errorToast from "helpers/errorToast";
import Input from "components/atoms/Input/Input";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import { getGuestUsers, inviteGuestUser } from "api";
import ManageAccessUserItem from "./components/ManageAccessUserItem";

const ManageAccessDialog = function ({ isOpen, close, shipmentId }) {
  const methods = useForm();
  const intl = useIntl();

  const [isLoading, setIsLoading] = useState(false);
  const [accessDetails, setAccessDetails] = useState({});
  const [usersPerCompany, setUsersPerCompany] = useState([]);
  const [guestEmail, setGuestEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setGuestEmail("");
    setResponseMessage("");
    setErrorMessage("");
    setUsersPerCompany([]);
    if (isOpen) {
      setIsLoading(true);
      getListOfGuestUsers();
    }
  }, [isOpen]);

  function getListOfGuestUsers() {
    getGuestUsers(shipmentId)
      .then((res) => {
        setAccessDetails(res.data);
        setIsLoading(false);

        if (res.data.guests.length > 0) {
          const formattedData = {};
          const otherUsers = [];

          res.data.guests.forEach((item) => {
            const companyKey = item.company_name ? item.company_name : "Other users";
            if (companyKey === "Other users") otherUsers.push(item);
            else {
              if (!formattedData[companyKey]) formattedData[companyKey] = [];
              formattedData[companyKey].push(item);
            }
          });
          const companiesArray = Object.entries(formattedData).map(([companyName, users]) => ({
            company: companyName,
            users,
          }));
          if (otherUsers.length > 0) companiesArray.push({ company: "Other users", users: otherUsers });
          setUsersPerCompany(companiesArray);
        } else {
          setUsersPerCompany([]);
        }
      })
      .catch((err) => errorToast(err, "Could not retrieve guest users."));
  }

  function inviteGuest(e) {
    setIsInviting(true);
    e.preventDefault();
    setErrorMessage("");
    inviteGuestUser(shipmentId, { email: guestEmail })
      .then((res) => {
        if (res.status === 201) {
          setResponseMessage(res.data.response_message);
          getListOfGuestUsers();
        }
      })
      .catch((err) => {
        if (err.response.status === 500)
          setErrorMessage(
            intl.formatMessage({
              id: "components.molecules.top-nav.manageAccessDialog.couldNotInviteUser",
              defaultMessage: "Could not invite user.",
            }),
          );
        else if (err.response.status === 400 && err.response.data.error_code === "USER_ALREADY_HAS_ACCESS") {
          setErrorMessage(
            intl.formatMessage({
              id: "components.molecules.top-nav.manageAccessDialog.couldNotInviteAsUserHasAccess",
              defaultMessage: "User already has access to this shipment.",
            }),
          );
        }
        else setErrorMessage(err.response.data.email);
      })
      .finally(() => setIsInviting(false));
    setGuestEmail("");
  }

  return (
    <FormProvider {...methods}>
      <Dialog
        title={
          <FormattedMessage
            id="components.molecules.top-nav.manageAccessDialog.manageAccess"
            defaultMessage="Manage access"
          />
        }
        show={isOpen}
        onHide={close}
        isLoading={isLoading}
      >
        <div style={{ width: "600px" }}>
          <div className="d-flex pt-2 border-bottom-light" style={{ marginBottom: "10px" }}>
            <Col sm={4} className="font-13 font-weight-bold">
              <FormattedMessage
                id="components.molecules.top-nav.manageAccessDialog.regularMember"
                defaultMessage="Regular members"
              />
            </Col>
            <Col sm={8} className="d-flex">
              {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
              {Object.entries(accessDetails.users || {}).map(([key, value]) => (
                <Col className="d-flex flex-column p-0 mb-3">
                  <span className="font-weight-bold font-11">{value.company_name}</span>
                  <FormattedMessage
                    id="components.molecules.top-nav.manageAccessDialog.usersWithAccess"
                    values={{ count: value.users_with_access }}
                    defaultMessage="{count} users with access"
                  />
                </Col>
              ))}
            </Col>
          </div>

          <div className="mt-3 d-flex  pt-2 pb-2 border-bottom-light guest-member-container">
            <Col sm={4}>
              <div className="font-13 font-weight-bold mb-xxs">
                <FormattedMessage
                  id="components.molecules.top-nav.manageAccessDialog.guestMembers"
                  defaultMessage="Guest members"
                />
              </div>
              <div className="text-xs font-regular text-tertiary">
                <FormattedMessage
                  id="components.molecules.top-nav.manageAccessDialog.guestMemberDescription"
                  defaultMessage="Can only view shipment details, contact details, comments, and updates."
                />
              </div>
            </Col>
            <Col sm={8} className="d-flex" style={{ width: "280px" }}>
              <div className="d-flex flex-column">
                <div className="user-container">
                  {usersPerCompany.map((company) => (
                    <ManageAccessUserItem
                      company={company}
                      shipmentId={shipmentId}
                      setErrorMessage={setErrorMessage}
                      setResponseMessage={setResponseMessage}
                      getListOfGuestUsers={getListOfGuestUsers}
                    />
                  ))}
                </div>
              </div>
            </Col>
          </div>

          <div className="mt-3 d-flex  pt-2 pb-2">
            <Col sm={4} className="font-13 font-weight-bold">
              <FormattedMessage
                id="components.molecules.top-nav.manageAccessDialog.inviteMember"
                defaultMessage="Invite member"
              />
            </Col>
            <Col className="d-flex flex-column font-11 mb-3" style={{ width: "280px" }}>
              <span className="mb-2">
                <FormattedMessage id="general.email" defaultMessage="Email" />
              </span>
              <div>
                <form className="d-flex align-items-start" onSubmit={inviteGuest}>
                  <Input
                    placeholder="Email"
                    onChange={(e) => setGuestEmail(e.target.value)}
                    value={guestEmail}
                    type="email"
                    eventContext="Guest Access Dialog"
                    eventName="Email"
                    className="input-width-md rounded-input"
                    data-testid={testIds.SHIPMENT_DASHBOARD_INVITE_GUEST_INPUT}
                  />

                  <Button
                    variant="secondary"
                    isLoading={isInviting}
                    style={{ transform: "translateX(-110px)", height: "26px" }}
                    type="submit"
                    data-testid={testIds.SHIPMENT_DASHBOARD_INVITE_GUEST_SUBMIT_BUTTON}
                  >
                    <FormattedMessage id="quote-request.invite" defaultMessage="Invite" />
                  </Button>
                </form>
              </div>
              {responseMessage && <p className="text-success mt-3 mb-0">{responseMessage}</p>}
              {errorMessage && <p className="text-danger mt-3 mb-0">{errorMessage}</p>}
            </Col>
          </div>
          <div className="w-100 mt-5 d-flex flex-div justify-content-between">
            <Button
              className="ml-auto"
              label={<FormattedMessage id="general.done" defaultMessage="Done" />}
              variant="success"
              onClick={close}
              size="lg"
            />
          </div>
        </div>
      </Dialog>
    </FormProvider>
  );
};

ManageAccessDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  shipmentId: PropTypes.string.isRequired,
};

export default ManageAccessDialog;
